// src/pages/BusinessSlotBooking.jsx

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import useCockpitGuard from "../hooks/useCockpitGuard";

export default function BusinessSlotBooking() {
  const { flyerId, slotNumber } = useParams();

  const {
    user,
    profile,
    businessId,
    loading: authLoading,
  } = useCockpitGuard();

  const [slot, setSlot] = useState(null);
  const [sectorIntel, setSectorIntel] = useState(null);
  const [sectorCode, setSectorCode] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!flyerId || !slotNumber) {
      setError("Missing flyer or slot information.");
      return;
    }

    if (authLoading) return;

    if (!user || !profile || !businessId) {
      setError(
        "You must be logged in as a business to book a slot."
      );
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setBusy(true);
        setError(null);

        const numericSlotNumber = Number(slotNumber);
        const numericBusinessId = Number(businessId);

        if (!Number.isInteger(numericSlotNumber)) {
          throw new Error("Invalid slot number.");
        }

        const {
          data: slotRow,
          error: slotError,
        } = await supabase
          .from("flyer_slots")
          .select("*")
          .eq("flyer_id", flyerId)
          .eq("slot_number", numericSlotNumber)
          .maybeSingle();

        if (slotError) {
          throw slotError;
        }

        if (!slotRow) {
          throw new Error(
            `Slot ${slotNumber} was not found for this flyer.`
          );
        }

        const existingBusinessId =
          slotRow.business_id === null
            ? null
            : Number(slotRow.business_id);

        if (
          existingBusinessId !== null &&
          existingBusinessId !== numericBusinessId
        ) {
          throw new Error(
            "This slot is already booked by another business."
          );
        }

        if (slotRow.cancelled) {
          throw new Error(
            "This slot has been cancelled and cannot be booked."
          );
        }

        const {
          data: flyerRow,
          error: flyerError,
        } = await supabase
          .from("flyers")
          .select("postcode_sector, campaign_id")
          .eq("id", flyerId)
          .maybeSingle();

        if (flyerError) {
          throw flyerError;
        }

        if (!flyerRow) {
          throw new Error(
            `Flyer ${flyerId} could not be found.`
          );
        }

        if (!flyerRow.postcode_sector) {
          throw new Error(
            "This flyer has no postcode sector assigned."
          );
        }

        const resolvedSector = String(
          flyerRow.postcode_sector
        )
          .trim()
          .toUpperCase()
          .replace(/\s+/g, " ");

        const {
          data: householdRow,
          error: householdError,
        } = await supabase
          .from("postcode_sectors")
          .select("postcode_sector, household_count")
          .eq("postcode_sector", resolvedSector)
          .maybeSingle();

        if (householdError) {
          throw householdError;
        }

        if (!householdRow) {
          throw new Error(
            `No household data found for ${resolvedSector}.`
          );
        }

        const householdCount = Number(
          householdRow.household_count
        );

        if (
          !Number.isFinite(householdCount) ||
          householdCount <= 0
        ) {
          throw new Error(
            `The household count for ${resolvedSector} is invalid.`
          );
        }

        const amountPence = Math.round(householdCount);
        const pricePounds = amountPence / 100;

        const predictedRedemptions = Math.round(
          householdCount * 0.025
        );

        const roiValue =
          predictedRedemptions * 10;

        console.log("[BOOKING INTELLIGENCE]", {
          flyerId,
          slotNumber: numericSlotNumber,
          campaignId: flyerRow.campaign_id,
          sector: resolvedSector,
          households: householdCount,
          amountPence,
          price: pricePounds,
          predictedRedemptions,
          roi: roiValue,
        });

        if (cancelled) return;

        setSlot(slotRow);
        setSectorCode(resolvedSector);
        setCampaignId(flyerRow.campaign_id || null);

        setSectorIntel({
          household_count: householdCount,
          predicted_redemptions:
            predictedRedemptions,
          roi_estimate: roiValue,
          amount_pence: amountPence,
          price: pricePounds,
        });
      } catch (loadError) {
        console.error(
          "[BusinessSlotBooking] Load failed:",
          loadError
        );

        if (!cancelled) {
          setSlot(null);
          setSectorIntel(null);
          setSectorCode(null);
          setCampaignId(null);

          setError(
            loadError?.message ||
              "Something went wrong while loading this slot."
          );
        }
      } finally {
        if (!cancelled) {
          setBusy(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    flyerId,
    slotNumber,
    authLoading,
    user?.id,
    profile?.name,
    businessId,
  ]);

  const households =
    sectorIntel?.household_count ?? 0;

  const predictedRedemptions =
    sectorIntel?.predicted_redemptions ?? 0;

  const estimatedROI =
    sectorIntel?.roi_estimate ?? 0;

  const price =
    sectorIntel?.price ?? 0;

  const formattedPrice =
    `£${Number(price).toFixed(2)}`;

  const handleConfirmAndPay = async () => {
    if (
      !slot ||
      !user?.id ||
      !businessId ||
      !sectorCode ||
      !sectorIntel
    ) {
      setError(
        "The booking information is incomplete. Please reload the page."
      );
      return;
    }

    try {
      setBusy(true);
      setError(null);

      const {
        data: latestSlot,
        error: latestSlotError,
      } = await supabase
        .from("flyer_slots")
        .select(
          "id, business_id, cancelled, status"
        )
        .eq("id", slot.id)
        .maybeSingle();

      if (latestSlotError) {
        throw latestSlotError;
      }

      if (!latestSlot) {
        throw new Error(
          "This slot could not be found."
        );
      }

      const latestBusinessId =
        latestSlot.business_id === null
          ? null
          : Number(latestSlot.business_id);

      if (
        latestBusinessId !== null &&
        latestBusinessId !== Number(businessId)
      ) {
        throw new Error(
          "This slot has just been booked by another business."
        );
      }

      if (latestSlot.cancelled) {
        throw new Error(
          "This slot has been cancelled."
        );
      }

      console.log(
        "[BusinessSlotBooking] Starting checkout:",
        {
          flyerId,
          slotNumber: Number(slotNumber),
          sector: sectorCode,
          campaignId,
        }
      );

      const {
        data: checkoutData,
        error: checkoutError,
      } = await supabase.functions.invoke(
        "create-checkout-session",
        {
          body: {
            flyerId,
            slotNumber: Number(slotNumber),
          },
        }
      );

      if (checkoutError) {
        console.error(
          "[BusinessSlotBooking] Checkout function failed:",
          checkoutError
        );

        let message =
          "Unable to start secure payment.";

        if (checkoutError.context) {
          try {
            const response =
              await checkoutError.context.json();

            if (response?.error) {
              message = response.error;
            }
          } catch {
            // Keep the default error message.
          }
        }

        throw new Error(message);
      }

      if (!checkoutData?.url) {
        throw new Error(
          "The payment service returned no checkout URL."
        );
      }

      window.location.assign(checkoutData.url);
    } catch (checkoutFailure) {
      console.error(
        "[BusinessSlotBooking] Payment failed:",
        checkoutFailure
      );

      setError(
        checkoutFailure?.message ||
          "Unexpected error while starting payment."
      );

      setBusy(false);
    }
  };

  if ((authLoading || busy) && !slot) {
    return (
      <div style={{ padding: "2rem" }}>
        Loading booking details…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "2rem",
          color: "red",
        }}
      >
        <h2>Booking error</h2>
        <p>{error}</p>

        <button
          type="button"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!slot) {
    return (
      <div style={{ padding: "2rem" }}>
        <h2>No slot found</h2>
        <p>
          We couldn’t find that slot for this
          flyer.
        </p>
      </div>
    );
  }

  const sectorLabel = sectorCode || "";

  return (
    <div
      style={{
        maxWidth: 640,
        margin: "2rem auto",
        padding: "1.5rem",
        border: "1px solid #eee",
        borderRadius: 12,
      }}
    >
      <h1>
        Book Slot #{slot.slot_number} 🚀
      </h1>

      <p>SCAN. SAVE. KEEP. 📍</p>

      <div
        style={{
          border: "2px solid #e53935",
          borderRadius: 12,
          padding: "1.5rem",
          marginTop: "1rem",
        }}
      >
        <h2>
          Summary of your advert placement
        </h2>

        {sectorLabel && (
          <p>
            🏠 Sector:{" "}
            <strong>{sectorLabel}</strong>
          </p>
        )}

        <p>
          🏠 Sector households:{" "}
          <strong>{households}</strong>
        </p>

        <p>
          🎯 Predicted redemptions:{" "}
          <strong>
            {predictedRedemptions}
          </strong>
        </p>

        <p>
          💷 Estimated ROI:{" "}
          <strong>
            £{Number(estimatedROI).toFixed(2)}
          </strong>
        </p>

        <p>
          📂 Slot category:{" "}
          <strong>
            {slot.category_name || "General"}
          </strong>
        </p>

        <p>
          🆔 Campaign ID:{" "}
          <strong>
            {campaignId || ""}
          </strong>
        </p>

        <p>
          💰 Price:{" "}
          <strong>{formattedPrice}</strong>
        </p>

        <p style={{ marginTop: "1rem" }}>
          Your advert will appear in this
          postcode sector’s flyer. Delivery
          follows your 30-day print + Royal
          Mail lag. 📬
        </p>

        <button
          type="button"
          onClick={handleConfirmAndPay}
          disabled={busy}
          style={{
            marginTop: "1.5rem",
            width: "100%",
            padding: "0.75rem 1rem",
            backgroundColor: "#e53935",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: "1rem",
            cursor: busy
              ? "not-allowed"
              : "pointer",
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy
            ? "Opening secure payment…"
            : `🚀 Confirm & Pay ${formattedPrice}`}
        </button>
      </div>
    </div>
  );
}